import RenderEntity, { RENDER_ENTITY_FORMAT5 } from 'ui-component/renders/RenderEntity';
import { lazy } from 'react';

// material-ui
import { Typography, Tooltip } from '@mui/material';

// third-party
import { format, parseJSON } from 'date-fns';

// project imports
import { appState, appGridActions } from 'store/behaviors/loader';
import capabilities from './capabilities';
import RenderEntityList, { RENDER_ENTITYLIST_FORMAT2 } from 'ui-component/renders/RenderEntityList';

// assets
import EditTwoToneIcon from '@mui/icons-material/EditTwoTone';

// lazy loaded components

import { renderUser } from 'ui-component/fields/EntitySelect/admin/UserSelect';

const UserSelect = lazy(() => import('ui-component/fields/EntitySelect/admin/UserSelect'));
const PartnershipTypeSelect = lazy(() => import('ui-component/fields/EntitySelect/crm/PartnershipTypeSelect'));

const initialState = {
    ...appState(capabilities),
    colDef: [
        {
            field: 'actions',
            type: 'actions',
            description: '',
            align: 'center',
            hideable: false,
            sortable: false,
            resizable: false,
            disableReorder: true,
            label: 'ui-comp-table-actions',
            getActions: (params, permissionData, onClickActions) => {
                const gridActions = appGridActions(capabilities, params?.row, permissionData, onClickActions);

                permissionData?.forEach((perm) => {
                    switch (perm) {
                        case 'canEdit':
                            if (params?.row?.deletedAt) {
                                return;
                            }

                            gridActions.push({
                                key: `actioneditinnewtab-row${params?.row?.id}`,
                                icon: <EditTwoToneIcon fontSize="small" />,
                                label: 'ui-comp-button-editinnewtab',
                                position: 2.1,
                                showInMenu: true,
                                onClick: () => {
                                    if (
                                        'onClickRowActionEditInNewTab' in (onClickActions || {}) &&
                                        typeof onClickActions.onClickRowActionEditInNewTab === 'function'
                                    ) {
                                        onClickActions.onClickRowActionEditInNewTab(params?.row);
                                    }
                                }
                            });
                            break;
                        default: // do nothing
                    }
                });

                return gridActions;
            }
        },
        {
            field: 'id',
            type: 'number',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            label: 'traits-appentity-id'
        },
        {
            field: 'code',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            label: 'traits-appentityhasautogencode-code'
        },
        {
            field: 'name',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            flex: 1,
            label: 'traits-appentityisdescribable-name'
        },
        {
            field: 'description',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: false,
            resizable: true,
            exportable: true,
            label: 'traits-appentityisdescribable-description'
        },
        {
            field: 'vatin',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            label: 'entity-crm-client-vatin'
        },
        {
            field: 'partnershipType',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            renderCell: (params) =>
                params?.value && (
                    <RenderEntity
                        entityName="partnershipType"
                        renderPattern="<name>"
                        renderTooltipPattern="[<acronym>] <name>"
                        renderFormat={RENDER_ENTITY_FORMAT5}
                        icon={params?.row?.partnershipType?.icon}
                        color={params?.row?.partnershipType?.color}
                        {...params}
                    />
                ),
            label: 'entity-crm-partnershiptype'
        },
        {
            field: 'mainAddress',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: false,
            resizable: true,
            exportable: true,
            label: 'entity-crm-client-mainaddress'
        },
        {
            field: 'mainTown',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: false,
            resizable: true,
            exportable: true,
            label: 'entity-crm-client-maintown'
        },
        {
            field: 'mainEmails',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: false,
            resizable: true,
            exportable: true,
            label: 'entity-crm-client-mainemails'
        },
        {
            field: 'mainPhoneNumbers',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: false,
            resizable: true,
            exportable: true,
            renderCell: (params) => (
                <RenderEntityList entityName="phoneNumber" renderPattern="<number>" renderFormat={RENDER_ENTITYLIST_FORMAT2} {...params} />
            ),
            label: 'entity-crm-client-mainphonenumbers'
        },
        {
            field: 'contacts',
            type: 'singleSelect',
            description: '',
            align: 'center',
            hideable: true,
            sortable: false,
            resizable: true,
            exportable: true,
            renderCell: (params) => (
                <RenderEntityList
                    entityName="contact"
                    renderPattern="<name> <lastName>"
                    renderFormat={RENDER_ENTITYLIST_FORMAT2}
                    {...params}
                />
            ),
            label: 'entity-crm-contact-plural'
        },
        {
            field: 'locations',
            type: 'singleSelect',
            description: '',
            align: 'center',
            hideable: true,
            sortable: false,
            resizable: true,
            exportable: true,
            renderCell: (params) => (
                <RenderEntityList
                    entityName="location"
                    renderPattern="<address>, <municipality.name>"
                    renderFormat={RENDER_ENTITYLIST_FORMAT2}
                    {...params}
                />
            ),
            label: 'entity-common-location-plural'
        },
        {
            field: 'contracts',
            type: 'singleSelect',
            description: '',
            align: 'center',
            hideable: true,
            sortable: false,
            resizable: true,
            exportable: true,
            renderCell: (params) => (
                <RenderEntityList
                    entityName="contract"
                    renderPattern="[<code>] <name>"
                    renderFormat={RENDER_ENTITYLIST_FORMAT2}
                    {...params}
                />
            ),
            label: 'entity-crm-contract-plural'
        },
        {
            field: 'businessOpportunityCount',
            type: 'number',
            description: '',
            align: 'center',
            hideable: true,
            sortable: false,
            resizable: true,
            exportable: true,
            label: 'entity-workmgmt-businessopportunity-plural'
        },
        {
            field: 'workOrderCount',
            type: 'number',
            description: '',
            align: 'center',
            hideable: true,
            sortable: false,
            exportable: true,
            resizable: true,
            label: 'entity-workmgmt-workorder-plural'
        },
        {
            field: 'pendingTicketCount',
            type: 'number',
            description: '',
            align: 'center',
            hideable: true,
            sortable: false,
            resizable: true,
            exportable: true,
            label: 'entity-workmgmt-ticket-plural'
        },
        {
            field: 'signedTicketCount',
            type: 'number',
            description: '',
            align: 'center',
            hideable: true,
            sortable: false,
            resizable: true,
            exportable: true,
            label: 'entity-workmgmt-ticket-plural'
        },
        {
            field: 'createdAt',
            type: 'dateTime',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            valueGetter: (params) => params?.value && parseJSON(params.value),
            renderCell: (params) =>
                params?.value && (
                    <Tooltip title={format(new Date(params.value), 'd/MM/yyyy HH:mm')}>
                        <Typography
                            variant="inherit"
                            color="inherit"
                            sx={{
                                overflow: 'hidden',
                                display: 'block',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {format(new Date(params.value), 'd/MM/yyyy HH:mm')}
                        </Typography>
                    </Tooltip>
                ),
            label: 'traits-appentity-createdat'
        },
        {
            field: 'createdBy',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            renderCell: (params) => renderUser(params?.value, 'fullName'),
            label: 'traits-appentity-createdby'
        },
        {
            field: 'updatedAt',
            type: 'dateTime',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            valueGetter: (params) => params?.value && parseJSON(params.value),
            renderCell: (params) =>
                params?.value && (
                    <Tooltip title={format(new Date(params.value), 'd/MM/yyyy HH:mm')}>
                        <Typography
                            variant="inherit"
                            color="inherit"
                            sx={{
                                overflow: 'hidden',
                                display: 'block',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {format(new Date(params.value), 'd/MM/yyyy HH:mm')}
                        </Typography>
                    </Tooltip>
                ),
            label: 'traits-appentity-updatedat'
        },
        {
            field: 'updatedBy',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            renderCell: (params) => renderUser(params?.value, 'fullName'),
            label: 'traits-appentity-updatedby'
        },
        {
            field: 'deletedAt',
            type: 'dateTime',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            valueGetter: (params) => params?.value && parseJSON(params.value),
            renderCell: (params) =>
                params?.value && (
                    <Tooltip title={format(new Date(params.value), 'd/MM/yyyy HH:mm')}>
                        <Typography
                            variant="inherit"
                            color="inherit"
                            sx={{
                                overflow: 'hidden',
                                display: 'block',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {format(new Date(params.value), 'd/MM/yyyy HH:mm')}
                        </Typography>
                    </Tooltip>
                ),
            label: 'traits-appentityissafedeletable-deletedat'
        },
        {
            field: 'deletedBy',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            renderCell: (params) => renderUser(params?.value, 'fullName'),
            label: 'traits-appentityissafedeletable-deletedby'
        }
    ],
    dataGridState: {
        columns: {
            columnVisibilityModel: {
                id: false,
                code: true,
                name: true,
                description: false,
                vatin: true,
                partnershipType: true,
                mainAddress: false,
                mainTown: false,
                mainEmails: false,
                mainPhoneNumbers: false,
                contacts: false,
                locations: false,
                contracts: false,
                businessOpportunityCount: false,
                workOrderCount: false,
                pendingTicketCount: false,
                signedTicketCount: false,
                createdAt: true,
                createdBy: true,
                updatedAt: false,
                updatedBy: false,
                deletedAt: false,
                deletedBy: false
            },
            orderedFields: [
                'actions',
                'id',
                'code',
                'name',
                'description',
                'vatin',
                'partnershipType',
                'mainAddress',
                'mainTown',
                'mainEmails',
                'mainPhoneNumbers',
                'contacts',
                'locations',
                'contracts',
                'businessOpportunityCount',
                'workOrderCount',
                'pendingTicketCount',
                'signedTicketCount',
                'createdAt',
                'createdBy',
                'updatedAt',
                'updatedBy',
                'deletedAt',
                'deletedBy'
            ],
            dimensions: {
                actions: { width: 60 },
                id: { width: 100 },
                code: { width: 100 },
                name: { width: 150 },
                description: { width: 100 },
                vatin: { width: 150 },
                partnershipType: { width: 100 },
                mainAddress: { width: 100 },
                mainTown: { width: 100 },
                mainEmails: { width: 100 },
                mainPhoneNumbers: { width: 100 },
                contacts: { width: 100 },
                locations: { width: 100 },
                contracts: { width: 100 },
                businessOpportunityCount: { width: 100 },
                workOrderCount: { width: 100 },
                pendingTicketCount: { width: 100 },
                signedTicketCount: { width: 100 },
                createdAt: { width: 100 },
                createdBy: { width: 100 },
                updatedAt: { width: 100 },
                updatedBy: { width: 100 },
                deletedAt: { width: 100 },
                deletedBy: { width: 100 }
            }
        },
        sorting: {
            sortModel: [{ field: 'id', sort: 'desc' }]
        },
        toolbarFilters: { includeSafeDeleted: false }
    },
    filterDef: [
        {
            filterType: 'string',
            field: { fieldName: 'code', fieldType: 'simple' },
            label: 'traits-appentityhasautogencode-code'
        },
        {
            filterType: 'string',
            field: { fieldName: 'name', fieldType: 'simple' },
            label: 'traits-appentityisdescribable-name'
        },
        {
            filterType: 'string',
            field: { fieldName: 'description', fieldType: 'simple' },
            label: 'traits-appentityisdescribable-description'
        },
        {
            filterType: 'string',
            field: { fieldName: 'vatin', fieldType: 'simple' },
            label: 'entity-crm-client-vatin'
        },
        {
            filterType: 'reference',
            field: { fieldName: 'partnershipTypeId', fieldType: 'simple' },
            label: 'entity-crm-partnershiptype',
            renderField: (params) => <PartnershipTypeSelect key="filterClient-partnershipType" multiple {...params} />
        },
        {
            filterType: 'dateTime',
            field: { fieldName: 'createdAt', fieldType: 'simple' },
            label: 'traits-appentity-createdat'
        },
        {
            filterType: 'reference',
            field: { fieldName: 'createdBy', fieldType: 'simple', outputFieldName: 'createdById' },
            label: 'traits-appentity-createdby',
            renderField: (params) => <UserSelect key="filterClient-createdBy" multiple displayMode="fullName" {...params} />
        },
        {
            filterType: 'dateTime',
            field: { fieldName: 'updatedAt', fieldType: 'simple' },
            label: 'traits-appentity-updatedat'
        },
        {
            filterType: 'reference',
            field: { fieldName: 'updatedBy', fieldType: 'simple', outputFieldName: 'updatedById' },
            label: 'traits-appentity-updatedby',
            renderField: (params) => <UserSelect key="filterClient-updatedBy" multiple displayMode="fullName" {...params} />
        },
        {
            filterType: 'dateTime',
            field: { fieldName: 'deletedAt', fieldType: 'simple' },
            label: 'traits-appentityissafedeletable-deletedat'
        },
        {
            filterType: 'reference',
            field: { fieldName: 'deletedBy', fieldType: 'simple', outputFieldName: 'deletedById' },
            label: 'traits-appentityissafedeletable-deletedby',
            renderField: (params) => <UserSelect key="filterClient-deletedBy" multiple displayMode="fullName" {...params} />
        }
    ],
    quickSearchFilterDef: {
        placeholder: 'ui-comp-quicksearchplaceholder-client',
        fields: [
            { fieldName: 'code', fieldType: 'simple' },
            { fieldName: 'name', fieldType: 'simple' },
            { fieldName: 'vatin', fieldType: 'simple' },
            { fieldName: 'description', fieldType: 'simple' },
            { fieldName: 'partnershipTypeAcronym', fieldType: 'simple' },
            { fieldName: 'partnershipTypeName', fieldType: 'simple' }
        ]
    },
    sortingDef: [
        { fieldName: 'id', fieldType: 'simple' },
        { fieldName: 'code', fieldType: 'simple' },
        { fieldName: 'name', fieldType: 'simple' },
        { fieldName: 'vatin', fieldType: 'simple' },
        { fieldName: 'partnershipTypeId', fieldType: 'simple' },
        { fieldName: 'createdAt', fieldType: 'simple' },
        { fieldName: 'createdBy', fieldType: 'simple' },
        { fieldName: 'updatedAt', fieldType: 'simple' },
        { fieldName: 'updatedBy', fieldType: 'simple' },
        { fieldName: 'deletedAt', fieldType: 'simple' },
        { fieldName: 'deletedBy', fieldType: 'simple' }
    ]
};

export default initialState;
